import { useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/**
 * Google reCAPTCHA V2 checkbox widget.
 * Props:
 *   onVerify(token) — called when user passes the challenge
 *   onExpire()      — called when the token expires
 *   theme           — "light" | "dark" (default: "light")
 */
export default function Recaptcha({ onVerify, onExpire, theme = "light" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onExpire });

  callbacksRef.current = { onVerify, onExpire };

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const scriptId = "google-recaptcha-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    let mounted = true;

    const renderWidget = () => {
      if (!mounted || !window.grecaptcha || !containerRef.current) return;
      if (widgetIdRef.current !== null) return;

      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => callbacksRef.current.onVerify?.(token),
        "expired-callback": () => callbacksRef.current.onExpire?.(),
        theme,
      });
    };

    if (window.grecaptcha && window.grecaptcha.render) {
      renderWidget();
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(interval);
          renderWidget();
        }
      }, 100);
      return () => {
        mounted = false;
        clearInterval(interval);
        if (widgetIdRef.current !== null && window.grecaptcha) {
          try { window.grecaptcha.reset(widgetIdRef.current); } catch {}
          widgetIdRef.current = null;
        }
      };
    }

    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && window.grecaptcha) {
        try { window.grecaptcha.reset(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [theme]);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} className="flex justify-center" />;
}
